<template>
  <div
    class="ncrm-preferred-view-tabs"
    role="tablist"
    :aria-label="ariaLabel || $t('ncrm.shared.viewPreferences.tabsAriaLabel')"
  >
    <div
      v-for="item in orderedItems"
      :key="item.value"
      class="ncrm-preferred-view-tabs__tab"
      :class="{
        'ncrm-preferred-view-tabs__tab--selected': item.value === modelValue,
        'ncrm-preferred-view-tabs__tab--preferred': isPreferred(item.value),
      }"
      :ref="(el) => setTabRef(item.value, el)"
      role="tab"
      :tabindex="item.value === activeValue ? 0 : -1"
      :aria-selected="item.value === modelValue ? 'true' : 'false'"
      @click="selectView(item.value)"
      @keydown="handleTabKeydown($event, item.value)"
    >
      <span class="ncrm-preferred-view-tabs__label">{{ item.label }}</span>
      <span
        v-if="orderedItems.length > 1"
        class="ncrm-preferred-view-tabs__pin"
        :class="{ 'ncrm-preferred-view-tabs__pin--preferred': isPreferred(item.value) }"
        role="button"
        :tabindex="item.value === activeValue ? 0 : -1"
        :aria-label="pinTitle(item)"
        :title="pinTitle(item)"
        @click.stop="setDefault(item.value)"
        @keydown="handlePinKeydown($event, item.value)"
      >
        <v-icon size="16">
          {{ isPreferred(item.value) ? "mdi-pin" : "mdi-pin-outline" }}
        </v-icon>
      </span>
    </div>
  </div>
</template>

<script>
import { reorderPreferredViewItems } from "@/Utile/ncrmViewPreferences.mjs";
import { nextTabValue } from "@/Utile/rovingTabs.mjs";

export default {
  name: "NcrmPreferredViewTabs",
  props: {
    modelValue: {
      type: String,
      default: null,
    },
    items: {
      type: Array,
      default: () => [],
    },
    preferredValue: {
      type: String,
      default: null,
    },
    ariaLabel: {
      type: String,
      default: null,
    },
  },
  emits: ["update:modelValue", "set-default"],
  computed: {
    orderedItems() {
      return reorderPreferredViewItems(this.items, this.preferredValue);
    },
    // Roving tabindex (WAI-ARIA tablist): un singur tab în ordinea de Tab, cel selectat (sau primul, dacă selecția nu e în listă).
    activeValue() {
      const values = this.orderedItems.map((item) => item.value);
      return values.includes(this.modelValue) ? this.modelValue : values[0] ?? null;
    },
  },
  created() {
    this.tabRefs = {};
  },
  methods: {
    setTabRef(value, el) {
      if (el) this.tabRefs[value] = el;
      else delete this.tabRefs[value];
    },
    isPreferred(value) {
      return String(value || "") === String(this.preferredValue || "");
    },
    selectView(value) {
      this.$emit("update:modelValue", value);
    },
    setDefault(value) {
      this.$emit("set-default", value);
    },
    handleTabKeydown(event, value) {
      if (event.target !== event.currentTarget) return; // tastele pin-ului sunt ale pin-ului
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        this.selectView(value);
        return;
      }
      // ArrowLeft / ArrowRight (circular), Home, End: selecția urmărește focusul, ca în `NcrmSegmentedControl`.
      const next = nextTabValue(this.orderedItems.map((item) => item.value), value, event.key);
      if (next === null) return;
      event.preventDefault();
      this.selectView(next);
      this.$nextTick(() => this.tabRefs[next]?.focus());
    },
    handlePinKeydown(event, value) {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      this.setDefault(value);
    },
    pinTitle(item) {
      if (this.isPreferred(item?.value)) {
        return this.$t("ncrm.shared.viewPreferences.currentDefault", {
          view: item?.label || "-",
        });
      }
      return this.$t("ncrm.shared.viewPreferences.setAsDefault", {
        view: item?.label || "-",
      });
    },
  },
};
</script>

<style scoped>
.ncrm-preferred-view-tabs {
  display: inline-flex;
  align-items: stretch;
  gap: 22px;
  flex-wrap: wrap;
  border-bottom: 1px solid var(--ncrm-color-border-subtle);
}

.ncrm-preferred-view-tabs__tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 44px;
  padding: 0 0 10px;
  border-bottom: 2px solid transparent;
  color: rgba(44, 62, 80, 0.72);
  cursor: pointer;
  outline: none;
  transition: color 0.18s ease, border-color 0.18s ease;
}

.ncrm-preferred-view-tabs__tab:hover,
.ncrm-preferred-view-tabs__tab:focus-visible {
  color: rgba(var(--v-theme-primary), 1);
}

.ncrm-preferred-view-tabs__tab--selected {
  color: rgba(var(--v-theme-primary), 1);
  border-bottom-color: rgba(var(--v-theme-primary), 1);
}

.ncrm-preferred-view-tabs__label {
  font-size: 0.95rem;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.ncrm-preferred-view-tabs__pin {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 999px;
  color: rgba(44, 62, 80, 0.62);
  cursor: pointer;
  outline: none;
  transition: color 0.18s ease, background-color 0.18s ease;
}

.ncrm-preferred-view-tabs__pin:hover,
.ncrm-preferred-view-tabs__pin:focus-visible {
  color: rgba(var(--v-theme-primary), 1);
  background: rgba(var(--v-theme-primary), 0.1);
}

.ncrm-preferred-view-tabs__pin--preferred {
  color: rgba(var(--v-theme-primary), 1);
  background: rgba(var(--v-theme-primary), 0.1);
}

@media (max-width: 720px) {
  .ncrm-preferred-view-tabs {
    gap: 16px;
  }

  .ncrm-preferred-view-tabs__label {
    font-size: 0.88rem;
  }
}
</style>
